import {
  UserRouteV1Model,
  isRouteValidated
} from '@core/models/entities/implementations/user-route-v1';
import { RouteResources } from '@core/models/plan-edit-gesture.model';

import { TimelineRow } from '@features/components/visualization/timeline/models/timeline';

export interface KnownRouteResources extends RouteResources {
  name: string;
  validated: boolean;
  companyUid: string | null;
  /** The named registration the route holds per resource; a numbered copy counts as none. */
  registrationCodes: Record<'vehicle' | 'trailer' | 'driver', string | null>;
}

function resourcesOf(
  uid: string,
  route: UserRouteV1Model
): Pick<RouteResources, 'routeUid' | 'vehicleUid' | 'driverUid' | 'trailerUid'> {
  return {
    routeUid: uid,
    vehicleUid: route.vehicle_uid,
    driverUid: route.driver_uid ?? null,
    trailerUid: route.trailer_uid ?? null
  };
}

function registrationCodesOf(
  route: UserRouteV1Model
): KnownRouteResources['registrationCodes'] {
  return {
    vehicle: route.vehicle_registration?.code ?? null,
    trailer: route.trailer_registration?.code ?? null,
    driver: route.driver_registration?.code ?? null
  };
}

/** The shown routes first, then those only the live overlay holds, each with its current resources. */
export function knownRouteResources(
  rows: readonly TimelineRow[],
  overlay: Record<string, UserRouteV1Model>
): Map<string, KnownRouteResources> {
  const known = new Map<string, KnownRouteResources>();
  for (const row of rows) {
    if (!row.route) continue;
    known.set(row.uid, {
      ...resourcesOf(row.uid, row.route),
      name: row.name,
      validated: row.validated ?? false,
      companyUid: row.route.company_uid ?? null,
      registrationCodes: registrationCodesOf(row.route)
    });
  }
  for (const [uid, route] of Object.entries(overlay)) {
    if (known.has(uid) || !route?.vehicle_uid) continue;
    known.set(uid, {
      ...resourcesOf(uid, route),
      name: route.name ?? uid,
      validated: isRouteValidated(route),
      companyUid: route.company_uid ?? null,
      registrationCodes: registrationCodesOf(route)
    });
  }
  return known;
}
